<script>
  import { goto } from '$app/navigation';
  import Input from '$lib/components/form/Input.svelte';
  import { api } from '$lib/utils/api';
  import { createQuery } from '@tanstack/svelte-query';
  import { toStore } from 'svelte/store';
  import { twMerge } from 'tailwind-merge';

  let q = $state('');
  const showSearchResults = $derived(q.length > 0);

  const hitsFor = (indexUid) =>
    $searchResultQuery.data?.results.find((r) => r.indexUid === indexUid)
      ?.hits || [];
  const companyResults = $derived(hitsFor('companies'));
  const clientResults = $derived(hitsFor('clients'));
  const reportResults = $derived(hitsFor('reports'));
  let inputRef = $state();
  let searchContainerClasses = $derived(
    twMerge(
      `absolute bg-gray-1 dark:bg-gray-8 -right-6 translate-x-full -top-4 border border-gray-8 rounded-lg`,
      {
        hidden: !showSearchResults,
      },
    ),
  );
  let container = $state();

  const searchResultQuery = createQuery(
    toStore(() => ({
      queryKey: ['search-result', q],
      queryFn: () => {
        return api().get(`v2/search?q=${q}`);
      },
      enabled: !!q,
    })),
  );

  let noResults = $derived(
    companyResults.length === 0 &&
      clientResults.length === 0 &&
      reportResults.length === 0,
  );

  function handleRowClick(link) {
    q = '';
    goto(link, { invalidateAll: true });
  }

  function handleClientRowClick(clientId) {
    goto(`/clients/${clientId}`);
  }

  function handleKeyDownRow(event, link) {
    if (!showSearchResults) {
      return;
    }

    if (event.key === 'Enter' || event.keyCode === 13) {
      handleRowClick(link);
    }
  }

  function handleClientKeyDownRow(event, clientId) {
    if (!showSearchResults) {
      return;
    }

    if (event.key === 'Enter' || event.keyCode === 13) {
      handleClientRowClick(clientId);
    }
  }

  function modulus(n, m) {
    const remain = n % m;
    return Math.floor(remain >= 0 ? remain : remain + m);
  }

  function handleKeyDown(event) {
    if (!showSearchResults) {
      if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
        event.preventDefault();
        inputRef.focus();
      }
      return;
    }

    if (event.key === 'Escape' && showSearchResults) {
      q = '';
    }

    // Check for the arrow-down key
    if (['ArrowDown', 'ArrowUp'].includes(event.key)) {
      // Prevent the default behavior
      event.preventDefault();

      const focusableElements = Array.from(
        container.querySelectorAll(
          'a, button, details, input, select, textarea, [tabindex]:not([tabindex="-1"])',
        ),
      );
      const currentIndex = focusableElements.indexOf(document.activeElement);

      if (event.key === 'ArrowDown') {
        const index = modulus(currentIndex + 1, focusableElements.length);
        focusableElements[index].focus();
        return;
      } else if (event.key === 'ArrowUp') {
        const index = modulus(currentIndex - 1, focusableElements.length);
        focusableElements[index].focus();
        return;
      }
    }
  }
</script>

<svelte:window onkeydown={handleKeyDown} />
{#if showSearchResults}
  <button
    class="absolute inset-0 w-screen cursor-default bg-black opacity-50"
    onclick={() => (q = '')}
    tabindex={-1}
  ></button>
{/if}
<div class="relative">
  <Input type="text" placeholder="Sök..." bind:value={q} bind:this={inputRef} />
  {#if showSearchResults}
    <div class={searchContainerClasses} bind:this={container}>
      <div class="flex w-full flex-col divide-y-2">
        {#if noResults}
          <div class="p-4 italic">Inga resultat...</div>
        {/if}

        {#if companyResults.length !== 0}
          <div class="py-2">
            <div class="px-4 py-2 text-[18px]">Företag</div>
            <table class="w-full">
              {#each companyResults as company}
                <tr
                  class="cursor-pointer transition-all duration-150 ease-in-out hover:bg-gray-7"
                  onclick={() =>
                    handleRowClick(`/companies/${company.id}/testphases`)}
                  onkeydown={(event) =>
                    handleKeyDownRow(
                      event,
                      `/companies/${company.id}/testphases`,
                    )}
                  tabindex={0}
                >
                  <td class="w-16 whitespace-nowrap px-4 py-2 font-semibold">
                    {company.id}
                  </td>
                  <td class="whitespace-nowrap px-4 py-2 font-semibold">
                    {company.name}
                  </td>
                  <td class="whitespace-nowrap px-4 py-2">
                    Org Nr: {company.orgNr}
                  </td>
                </tr>
              {/each}
            </table>
          </div>
        {/if}

        {#if clientResults.length !== 0}
          <div class="py-2">
            <div class="px-4 py-2 text-[18px]">Patienter</div>
            <table class="w-full">
              {#each clientResults as client, i}
                <tr
                  class="cursor-pointer transition-all duration-150 ease-in-out hover:bg-gray-7"
                  onclick={() => handleClientRowClick(client.id)}
                  onkeydown={(event) =>
                    handleClientKeyDownRow(event, client.id)}
                  tabindex={0}
                >
                  <td class="w-16 whitespace-nowrap px-4 py-2 font-semibold">
                    {client.id}
                  </td>
                  <td
                    class="whitespace-nowrap px-4 py-2 font-semibold capitalize"
                  >
                    {client.name}
                  </td>
                  <td class="whitespace-nowrap px-4 py-2">{client.email}</td>
                </tr>
              {/each}
            </table>
          </div>
        {/if}

        {#if reportResults.length !== 0}
          <div class="py-2">
            <div class="px-4 py-2 text-[18px]">Rapporter</div>
            <table class="w-full">
              {#each reportResults as report, i}
                <tr
                  class="cursor-pointer transition-all duration-150 ease-in-out hover:bg-gray-7"
                  onclick={() => handleRowClick(`/reports/${report.id}`)}
                  onkeydown={(event) =>
                    handleKeyDownRow(event, `/reports/${report.id}`)}
                  tabindex={0}
                >
                  <td class="w-16 whitespace-nowrap px-4 py-2 font-semibold">
                    {report.id}
                  </td>
                  <td
                    class="whitespace-nowrap px-4 py-2 font-semibold capitalize"
                  >
                    {report.clientName}
                  </td>
                  <td class="whitespace-nowrap px-4 py-2">
                    LID: {report.lid}
                  </td>
                </tr>
              {/each}
            </table>
          </div>
        {/if}
      </div>
    </div>
  {/if}
</div>
